import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import {
  FolderKanban,
  Calendar,
  Users,
  MoreVertical,
  ListTodo,
  Clock,
  AlertTriangle,
  Edit2,
  Settings,
  Trash2,
  FolderTree,
  CheckCircle,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { Project, ProjectStatus, TaskPriority } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';
import { projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

interface ProjectKanbanBoardProps {
  projects: Project[];
  isLoading: boolean;
  onProjectClick?: (projectId: string) => void;
  onEditProject?: (project: Project) => void;
  onDeleteProject?: (project: Project) => void;
  onCloseProject?: (project: Project) => void;
}

// Column configuration for project statuses
const columns = [
  { id: ProjectStatus.PLANNING, title: 'Planning', color: '#64748b', icon: FolderKanban },
  { id: ProjectStatus.ACTIVE, title: 'Active', color: '#2563eb', icon: Clock },
  { id: ProjectStatus.ON_HOLD, title: 'On Hold', color: '#f59e0b', icon: AlertTriangle },
  { id: ProjectStatus.COMPLETED, title: 'Completed', color: '#16a34a', icon: FolderTree },
];

// Priority colors (using TaskPriority enum values)
const priorityColors: Record<TaskPriority | string, string> = {
  [TaskPriority.P1]: '#ef4444', // Critical
  [TaskPriority.P2]: '#f59e0b', // High
  [TaskPriority.P3]: '#2563eb', // Medium
  [TaskPriority.P4]: '#64748b', // Low
  // Fallbacks for string values
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#2563eb',
  low: '#64748b',
};

/**
 * ProjectKanbanBoard
 * Drag-and-drop kanban board for project status management
 * Visual project tracking across status columns
 */
const ProjectKanbanBoard: React.FC<ProjectKanbanBoardProps> = ({
  projects,
  isLoading,
  onProjectClick,
  onEditProject,
  onDeleteProject,
  onCloseProject,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Update project status mutation
  const updateMutation = useMutation({
    mutationFn: ({ projectId, status }: { projectId: string; status: ProjectStatus }) =>
      projectService.updateProject(projectId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    },
  });

  // Group projects by status
  const getProjectsByStatus = useCallback(() => {
    const grouped: Record<ProjectStatus, Project[]> = {
      [ProjectStatus.PLANNING]: [],
      [ProjectStatus.ACTIVE]: [],
      [ProjectStatus.ON_HOLD]: [],
      [ProjectStatus.COMPLETED]: [],
      [ProjectStatus.CANCELLED]: [],
    };

    projects.forEach((project) => {
      if (grouped[project.status]) {
        grouped[project.status].push(project);
      }
    });

    return grouped;
  }, [projects]);

  const projectsByStatus = getProjectsByStatus();

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId as ProjectStatus;
    updateMutation.mutate({ projectId: draggableId, status: newStatus });
  };

  const handleProjectClick = (projectId: string) => {
    if (onProjectClick) {
      onProjectClick(projectId);
    } else {
      navigate(`/projects/${projectId}`);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedProject(null);
  };

  const handleEdit = () => {
    if (selectedProject && onEditProject) {
      onEditProject(selectedProject);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedProject && onDeleteProject) {
      onDeleteProject(selectedProject);
    }
    handleMenuClose();
  };

  const getTotalTasks = (project: Project) => project.taskCount ?? project.taskIds?.length ?? 0;
  const getCompletedTasks = (project: Project) => project.completedTaskCount ?? 0;
  const getCardAccentColor = (project: Project) =>
    project.color || columns.find((c) => c.id === project.status)?.color || '#64748b';

  // Task-based completion % (matches project detail page)
  const getProjectProgress = (project: Project): number => {
    if (project.status === ProjectStatus.COMPLETED) return 100;
    const total = getTotalTasks(project);
    const completed = getCompletedTasks(project);
    if (total > 0) {
      return Math.round((completed / total) * 100);
    }
    const fromApi = project.completionPercentage != null && !Number.isNaN(Number(project.completionPercentage))
      ? Number(project.completionPercentage)
      : null;
    if (fromApi !== null && fromApi > 0) {
      return Math.min(100, Math.max(0, fromApi));
    }
    if (!project.startDate || !project.endDate) return 0;
    const start = dayjs(project.startDate);
    const end = dayjs(project.endDate);
    const now = dayjs();
    if (!start.isValid() || !end.isValid()) return 0;
    if (now.isBefore(start)) return 0;
    if (now.isAfter(end)) return 100;
    const totalDays = end.diff(start, 'day');
    const elapsed = now.diff(start, 'day');
    if (totalDays <= 0) return 0;
    const pct = Math.round((elapsed / totalDays) * 100);
    return Number.isNaN(pct) ? 0 : Math.min(100, Math.max(0, pct));
  };

  // Get deadline status
  const getDeadlineStatus = (endDate: string, status: ProjectStatus) => {
    if (status === ProjectStatus.COMPLETED || status === ProjectStatus.CANCELLED) {
      return { color: 'text.secondary', label: 'Ended' };
    }
    
    const now = dayjs();
    const deadline = dayjs(endDate);
    const daysUntil = deadline.diff(now, 'day');

    if (daysUntil < 0) return { color: 'error.main', label: 'Overdue' };
    if (daysUntil === 0) return { color: 'error.main', label: 'Due today' };
    if (daysUntil <= 7) return { color: 'warning.main', label: `${daysUntil}d left` };
    return { color: 'text.secondary', label: dayjs(endDate).format('MMM D') };
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', pb: 2 }}>
        {columns.map((column) => (
          <Paper
            key={column.id}
            sx={{
              minWidth: 320,
              maxWidth: 320,
              bgcolor: 'background.default',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: column.color,
                }}
              />
              <Skeleton variant="text" width={100} />
            </Box>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={160}
                sx={{ mb: 1.5 }}
              />
            ))}
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflow: 'auto',
            pb: 2,
            minHeight: 500,
          }}
        >
          {columns.map((column) => {
            const columnProjects = projectsByStatus[column.id] || [];
            const ColumnIcon = column.icon;

            return (
              <Paper
                key={column.id}
                sx={{
                  minWidth: 320,
                  maxWidth: 320,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Column Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: `${column.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ColumnIcon size={16} style={{ color: column.color }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {column.title}
                    </Typography>
                    <Chip
                      label={columnProjects.length}
                      size="small"
                      sx={{
                        ml: 'auto',
                        height: 22,
                        fontSize: '0.75rem',
                        bgcolor: `${column.color}20`,
                        color: column.color,
                      }}
                    />
                  </Box>
                </Box>

                {/* Droppable Area */}
                <Droppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        minHeight: 200,
                        bgcolor: snapshot.isDraggingOver
                          ? `${column.color}10`
                          : 'transparent',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {columnProjects.map((project, index) => {
                        const progress = getProjectProgress(project);
                        const deadlineStatus = getDeadlineStatus(
                          project.endDate,
                          project.status
                        );

                        return (
                          <Draggable
                            key={project.id}
                            draggableId={project.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => handleProjectClick(project.id)}
                                sx={{
                                  p: 2,
                                  mb: 1.5,
                                  cursor: 'pointer',
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderLeft: `4px solid ${getCardAccentColor(project)}`,
                                  borderColor: snapshot.isDragging
                                    ? 'primary.main'
                                    : 'divider',
                                  boxShadow: snapshot.isDragging
                                    ? '0 8px 24px rgba(0,0,0,0.15)'
                                    : '0 1px 3px rgba(0,0,0,0.08)',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    borderColor: 'primary.light',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  },
                                }}
                              >
                                {/* Project Header */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 1.5,
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    {project.projRef && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', fontSize: '0.7rem', mb: 0.25 }}
                                      >
                                        {project.projRef}
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                      noWrap
                                      sx={{ mb: 0.5 }}
                                    >
                                      {project.name}
                                    </Typography>
                                    {project.description && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {project.description}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, project)}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <MoreVertical size={16} />
                                  </IconButton>
                                </Box>

                                {/* Completion progress */}
                                <Box sx={{ mb: 1.5, minHeight: 36 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      Completion
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color={progress === 100 ? 'success.main' : 'text.primary'}
                                    >
                                      {formatPercent(Math.round(progress))}
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, progress))}
                                    sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: 'grey.200',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor:
                                          progress === 100
                                            ? 'success.main'
                                            : progress > 70
                                            ? 'primary.main'
                                            : progress > 0
                                            ? 'warning.main'
                                            : 'grey.400',
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Tags */}
                                {project.tags && project.tags.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                                    {project.tags.slice(0, 3).map((tag) => (
                                      <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          bgcolor: 'grey.100',
                                        }}
                                      />
                                    ))}
                                    {project.tags.length > 3 && (
                                      <Chip
                                        label={`+${project.tags.length - 3}`}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          bgcolor: 'grey.200',
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}

                                {/* Close Project chip (non-completed only) */}
                                {project.status !== ProjectStatus.COMPLETED &&
                                  project.status !== ProjectStatus.CANCELLED &&
                                  onCloseProject && (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Chip
                                        icon={<CheckCircle size={12} />}
                                        label="Close Project"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onCloseProject(project);
                                        }}
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          bgcolor: 'success.50',
                                          color: 'success.main',
                                          '& .MuiChip-label': { px: 0.75 },
                                        }}
                                      />
                                    </Box>
                                  )}

                                {/* Footer */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    pt: 1.5,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {/* Deadline */}
                                  <Tooltip title="Due date">
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Calendar
                                        size={12}
                                        style={{ color: deadlineStatus.color }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{ color: deadlineStatus.color }}
                                      >
                                        {deadlineStatus.label}
                                      </Typography>
                                    </Box>
                                  </Tooltip>

                                  {/* Task Count */}
                                  <Tooltip title="Tasks">
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <ListTodo size={12} />
                                      <Typography variant="caption">
                                        {getTotalTasks(project)}
                                      </Typography>
                                    </Box>
                                  </Tooltip>

                                  {/* Priority */}
                                  <Chip
                                    label={project.priority}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      textTransform: 'capitalize',
                                      bgcolor: `${priorityColors[project.priority]}15`,
                                      color: priorityColors[project.priority],
                                    }}
                                  />

                                  {/* Team: show only when project has team/owner info from API */}
                                  {(project.ownerId || (project as { teamMemberIds?: string[] }).teamMemberIds?.length) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Users size={14} color="#64748b" />
                                      <Typography variant="caption" color="text.secondary">
                                        Team
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </Box>

                                {/* Risk Indicator */}
                                {project.riskLevel === 'high' && (
                                  <Box
                                    sx={{
                                      mt: 1.5,
                                      p: 1,
                                      bgcolor: 'error.50',
                                      borderRadius: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <AlertTriangle size={12} color="#dc2626" />
                                    <Typography
                                      variant="caption"
                                      color="error.main"
                                      fontWeight={500}
                                    >
                                      High Risk
                                    </Typography>
                                  </Box>
                                )}
                              </Paper>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}

                      {/* Empty State */}
                      {columnProjects.length === 0 && (
                        <Box
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            color: 'text.secondary',
                          }}
                        >
                          <FolderKanban
                            size={32}
                            style={{ opacity: 0.3, marginBottom: 8 }}
                          />
                          <Typography variant="body2">
                            No projects
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            );
          })}
        </Box>
      </DragDropContext>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {onEditProject && (
          <MenuItem onClick={handleEdit}>
            <Edit2 size={16} style={{ marginRight: 8 }} />
            Edit Project
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedProject) {
            navigate(`/projects/${selectedProject.id}`);
          }
          handleMenuClose();
        }}>
          <Settings size={16} style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
        {selectedProject &&
          selectedProject.status !== ProjectStatus.COMPLETED &&
          selectedProject.status !== ProjectStatus.CANCELLED &&
          onCloseProject && (
            <MenuItem
              onClick={() => {
                if (selectedProject) onCloseProject(selectedProject);
                handleMenuClose();
              }}
              sx={{ color: 'success.main' }}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              Close Project
            </MenuItem>
          )}
        {onDeleteProject && (
          <>
            <Divider />
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <Trash2 size={16} style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ProjectKanbanBoard;
